// doctor-frontend/src/pages/ApprovalDashboard.js
// Approvals / Returned-to-Edit dashboard with:
// - Phase 1 structural alignment: reposition as Reviewer Workbench while preserving all existing queues and actions
// - Correct page title for Interns on Returned tab
// - "Open" behavior: Interns on Returned -> EDITOR; reviewers -> read-only Visit Workspace review
// - Structured Return Visit for Correction dialog with section/field flags + reviewer instructions
// - Counters, filters, cache-busted fetches and legacy summary/PDF fallbacks preserved
// - Guided Activities (NVD, etc.) observer approval queue section (JR/SR/Faculty)
//
// FIX (Dec 2025, no regression):
// ✅ Preserve tenant slug on navigation by prefixing tenantBase() for routes
//    like /citizen/... and /intern/... (prevents redirect-to-login loops).
// ✅ Harden apiOrigin() so raw PDF fetch doesn't hit localhost:3000.
// ✅ NEW (Dec 2025): Pass "from" state on Review/Open navigations so Observer/Guided screens
//    can safely navigate back without losing tenant slug (prevents login loops on Back/Record Decision).
//
// FIX (Dec 2025, no regression):
// ✅ Guided activity queue: accept common backend list keys (queue/activities/results)
// ✅ Cache-bust reviewerQueue requests so "today's submissions" appear immediately
//
// PATCH (Jul 2026, no regression):
// ✅ Core Practice Area-safe display handling for guided activity metadata.
// ✅ Tenant slug fallback from storage for navigation/PDF fetches.
// ✅ Safer CMFM token selection for raw PDF fetches.
// ✅ Reviewer Workbench guided rows show mapped rotation metadata and open own work as evidence-only.
// ✅ Aug 2026: reviewer guided-history filters show All / Pending review / Returned / Approved without mixing My Work into queue status.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Chip, Button, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, Stack, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  MenuItem, Checkbox
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LaunchIcon from '@mui/icons-material/Launch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import { useLocation, useNavigate } from 'react-router-dom';
import http from '../services/http';
import { activityApi } from '../services/activityApi';

const safe = (v) => (v == null ? '' : String(v));
const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

function identifierText(value, preferredKeys = []) {
  if (value === undefined || value === null) return '';

  if (typeof value === 'object' && !Array.isArray(value)) {
    const keys = [
      ...preferredKeys,
      'visitId',
      'citizenId',
      'id',
      '_id',
      'value',
    ];
    for (const key of keys) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
      const hit = identifierText(value[key], preferredKeys);
      if (hit) return hit;
    }
    return '';
  }

  const text = String(value || '').trim();
  return text && text !== '[object Object]' ? text : '';
}

function visitIdFromRow(row = {}) {
  const candidates = [
    row?.visitId,
    row?.id,
    row?._id,
    row?.visit?.visitId,
    row?.visit?.id,
    row?.visit?._id,
  ];
  for (const candidate of candidates) {
    const hit = identifierText(candidate, ['visitId']);
    if (hit) return hit;
  }
  return '';
}

function citizenIdFromRow(row = {}) {
  const candidates = [
    row?.citizenId,
    row?.citizen?.citizenId,
    row?.citizen?.id,
    row?.citizen?._id,
    row?.citizen,
    row?.visit?.citizenId,
    row?.visit?.citizen,
  ];
  for (const candidate of candidates) {
    const hit = identifierText(candidate, ['citizenId']);
    if (hit) return hit;
  }
  return '';
}

const RETURN_SECTION_OPTIONS = [
  { value: 'history', label: 'History' },
  { value: 'examination', label: 'Examination' },
  { value: 'investigations', label: 'Investigations' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'general', label: 'Whole Visit / General' },
];

function emptyCorrectionItem() {
  return { section: '', field: '', instruction: '' };
}

function normalizeCorrectionItem(item = {}) {
  const section = String(item?.section || item?.sectionKey || item?.area || '').trim().toLowerCase();
  const field = sanitizeDisplayText(item?.field || item?.fieldKey || item?.label || item?.issue || '');
  const instruction = sanitizeDisplayText(
    item?.instruction || item?.comment || item?.message || item?.note || ''
  );
  return { section, field, instruction };
}

function correctionItemsFromRow(row = {}) {
  const candidates = [
    row?.correctionItems,
    row?.returnItems,
    row?.flaggedCorrections,
    row?.reviewCorrections,
    row?.prescription?.correctionItems,
    row?.prescription?.returnItems,
    row?.prescription?.flaggedCorrections,
  ];
  const source = candidates.find((value) => Array.isArray(value)) || [];
  return source.map(normalizeCorrectionItem).filter((item) => item.section || item.field || item.instruction);
}

function mergeCorrectionItems(...sources) {
  const seen = new Set();
  const merged = [];

  sources
    .flatMap((source) => (Array.isArray(source) ? source : []))
    .map(normalizeCorrectionItem)
    .filter((item) => item.section || item.field || item.instruction)
    .forEach((item) => {
      const key = [item.section, item.field, item.instruction]
        .map((value) => String(value || '').trim().toLowerCase())
        .join('|');
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(item);
    });

  return merged;
}

function correctionItemSummary(item = {}) {
  const normalized = normalizeCorrectionItem(item);
  const sectionLabel =
    RETURN_SECTION_OPTIONS.find((option) => option.value === normalized.section)?.label ||
    sanitizeDisplayText(normalized.section) ||
    'Visit';
  const target = normalized.field ? `${sectionLabel} → ${normalized.field}` : sectionLabel;
  return normalized.instruction ? `${target}: ${normalized.instruction}` : target;
}

function returnInstructionSummary(row = {}) {
  const note = sanitizeDisplayText(row?.returnNote || row?.note || row?.prescription?.returnNote || '');
  const items = correctionItemsFromRow(row);
  const itemText = items
    .map(correctionItemSummary)
    .filter(Boolean)
    .join(' | ');
  return [note, itemText].filter(Boolean).join(' | ');
}

function readStoredReviewFlags(visitId) {
  if (!visitId) return [];
  try {
    const raw = localStorage.getItem(`visitReviewFlags:${visitId}`);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.map(normalizeCorrectionItem).filter((item) => item.section || item.field || item.instruction)
      : [];
  } catch {
    return [];
  }
}

function clearStoredReviewFlags(visitId) {
  if (!visitId) return;
  try {
    localStorage.removeItem(`visitReviewFlags:${visitId}`);
  } catch {}
}

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = ['Entrustable', 'Professional', 'Activities'].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function sanitizeStringArray(values) {
  if (!Array.isArray(values)) return [];
  return values.map((value) => sanitizeDisplayText(value)).filter(Boolean);
}

function safeJsonParse(v) {
  try {
    return JSON.parse(v || 'null');
  } catch {
    return null;
  }
}

const roleOf = (u = {}) =>
  sanitizeDisplayText(
    u?.role ||
      u?.userType ||
      u?.user?.role ||
      u?.profile?.role ||
      ''
  )
    .toLowerCase()
    .trim();

const nameOf = (u = {}) =>
  sanitizeDisplayText(
    u?.name ||
      u?.fullName ||
      u?.displayName ||
      u?.email ||
      u?.username ||
      u?.user?.name ||
      u?.user?.fullName ||
      u?.user?.displayName ||
      u?.user?.email ||
      ''
  );

function normalizeStatusToken(s = '') {
  return String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[_\s]+/g, '-')
    .replace(/-+/g, '-');
}

const statusText = (s = '') => {
  const t = normalizeStatusToken(s);

  // Visit / prescription statuses
  if (['submitted', 'pending', 'awaiting-review'].includes(t)) return 'Awaiting review';
  if (t === 'approved') return 'Approved';
  if (t === 'returned') return 'Returned to edit';
  if (t === 'draft') return 'Draft';

  // Activity / observer statuses for guided activities
  if (t === 'observed-submitted') return 'Awaiting observer';
  if (t === 'observed-approved') return 'Observer approved';
  if (t === 'observed-returned') return 'Observer returned';
  if (t === 'observed-repeat' || t === 'repeat') return 'Repeat required';

  return sanitizeDisplayText(s) || '-';
};

const chipColor = (s = '') => {
  const t = normalizeStatusToken(s);
  if (['approved', 'observed-approved', 'observer-approved'].includes(t)) return 'success';
  if (['returned', 'observed-returned', 'observed-repeat', 'repeat', 'repeat-required', 'returned-to-edit'].includes(t)) return 'default';
  if (
    ['awaiting-review', 'pending', 'submitted', 'observed-submitted', 'awaiting-observer'].includes(t)
  ) {
    return 'warning';
  }
  return 'default';
};

const PARTICIPATION_MODES = [
  'observed',
  'assisted',
  'performed_supervised',
  'performed_independent',
];

function normalizeParticipationMode(value) {
  const s = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const aliases = {
    observe: 'observed', observer: 'observed', observed: 'observed',
    assist: 'assisted', assisted: 'assisted', participated: 'assisted', participant: 'assisted',
    performed: 'performed_supervised', supervised: 'performed_supervised',
    performed_supervised: 'performed_supervised', performed_under_supervision: 'performed_supervised',
    independent: 'performed_independent', performed_independent: 'performed_independent',
    independent_assigned_task: 'performed_independent',
  };
  const mapped = aliases[s] || s;
  return PARTICIPATION_MODES.includes(mapped) ? mapped : '';
}

function participationModeLabel(value) {
  const mode = normalizeParticipationMode(value);
  if (mode === 'observed') return 'Observed';
  if (mode === 'assisted') return 'Assisted / Participated';
  if (mode === 'performed_supervised') return 'Performed / Led under supervision';
  if (mode === 'performed_independent') return 'Independent assigned task';
  return '—';
}

function templateParticipationModes(template = {}) {
  return (Array.isArray(template?.participationModes) ? template.participationModes : [])
    .map((item) => ({
      ...(item || {}),
      key: normalizeParticipationMode(item?.key || item?.mode || item),
      label: sanitizeDisplayText(item?.label || participationModeLabel(item?.key || item)),
    }))
    .filter((item) => item.key && item.allowed !== false);
}

function participationModeConfig(template = {}, modeValue = '') {
  const mode = normalizeParticipationMode(modeValue);
  if (!mode) return null;
  return templateParticipationModes(template).find((item) => item.key === mode) || null;
}

const coerceList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;

  // Common variants used by different backends
  if (Array.isArray(payload?.queue)) return payload.queue;
  if (Array.isArray(payload?.activities)) return payload.activities;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;

  return [];
};

function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const key = store.key(i);
      if (key) out.push(key);
    }
    return out;
  } catch {
    return [];
  }
}

function readStoredUser() {
  const keys = [
    'loggedInUser',
    'user',
    'currentUser',
    'authUser',
    'cmfmUser',
    'portalUser',
  ];

  try {
    for (const key of keys) {
      const local = safeJsonParse(localStorage.getItem(key));
      const session = safeJsonParse(sessionStorage.getItem(key));
      const hit = local || session;
      if (hit) return hit?.user ? { ...hit.user, ...hit } : hit;
    }
  } catch {
    // ignore
  }

  return {};
}

function readTenantSlugFromStorage() {
  try {
    const candidates = [
      localStorage.getItem('activeTenantSlug'),
      localStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
    ];

    [safeJsonParse(localStorage.getItem('loggedInUser')), safeJsonParse(sessionStorage.getItem('loggedInUser'))]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(core?.tenantSlug, obj?.tenantSlug, core?.tenant?.slug, obj?.tenant?.slug);
      });

    return candidates
      .map((value) => String(value || '').trim())
      .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || '';
  } catch {
    return '';
  }
}

function getPortalToken() {
  const pickFrom = (store) => {
    try {
      const keys = listStorageKeys(store);
      const preferred = ['cmfm_token', 'cmfmToken', 'tenant_token', 'portal_token', 'authToken', 'jwt', 'token'];

      for (const name of preferred) {
        const hit = keys.find((key) => key.toLowerCase() === String(name).toLowerCase());
        if (hit) return store.getItem(hit);
      }

      const tenantToken = keys.find((key) => /cmfm|tenant|portal/i.test(key) && /token/i.test(key));
      if (tenantToken) return store.getItem(tenantToken);

      const nonOwnerCitizen = keys.find((key) => /token/i.test(key) && !/owner/i.test(key) && !/citizen/i.test(key));
      if (nonOwnerCitizen) return store.getItem(nonOwnerCitizen);

      return '';
    } catch {
      return '';
    }
  };

  return pickFrom(localStorage) || pickFrom(sessionStorage) || '';
}

function activityTitle(row = {}) {
  return sanitizeDisplayText(
    row?.templateTitle ||
      row?.title ||
      row?.templateName ||
      row?.activityType ||
      row?.activityTitle ||
      'Activity'
  );
}

function readablePersonName(value) {
  if (value === undefined || value === null) return '';

  if (typeof value === 'object' && !Array.isArray(value)) {
    const candidates = [
      value.name,
      value.fullName,
      value.displayName,
      value.email,
      value.username,
      value.user?.name,
      value.user?.fullName,
      value.user?.displayName,
      value.user?.email,
      value.profile?.name,
      value.profile?.fullName,
    ];
    for (const candidate of candidates) {
      const hit = readablePersonName(candidate);
      if (hit) return hit;
    }
    return '';
  }

  const text = sanitizeDisplayText(value);
  if (!text || text === '[object Object]' || /^[a-f0-9]{24}$/i.test(text)) return '';
  return text;
}

function stableId(value) {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    return stableId(value._id || value.id || value.$oid || '');
  }
  const text = String(value || '').trim();
  return /^[a-f0-9]{24}$/i.test(text) ? text : '';
}

function activityInternName(row = {}) {
  const candidates = [
    row?.internName,
    row?.internDisplayName,
    row?.internUser,
    row?.internUserId,
    row?.intern,
    row?.userName,
    row?.user,
    row?.submittedByName,
    row?.submittedBy,
  ];

  for (const candidate of candidates) {
    const hit = readablePersonName(candidate);
    if (hit) return hit;
  }
  return '';
}

function activityStatus(row = {}) {
  return row?.status || row?.workflowStatus || row?.approvalStatus || '';
}

function guidedReviewBucket(statusValue = '') {
  const status = normalizeStatusToken(statusValue);

  if (
    [
      'submitted',
      'pending',
      'awaiting-review',
      'awaiting-observer',
      'observed-submitted',
    ].includes(status)
  ) {
    return 'pending';
  }

  if (
    [
      'returned',
      'returned-to-edit',
      'observed-returned',
      'repeat',
      'observed-repeat',
      'repeat-required',
    ].includes(status)
  ) {
    return 'returned';
  }

  if (['approved', 'observed-approved', 'observer-approved'].includes(status)) {
    return 'approved';
  }

  return 'other';
}

function activityReportingTarget(row = {}) {
  const target = row?.reportingTarget && typeof row.reportingTarget === 'object' ? row.reportingTarget : {};
  return {
    section: sanitizeDisplayText(row?.corePracticeAreaTitle || row?.practiceAreaTitle || row?.finalGroup || target.section || ''),
    item: sanitizeDisplayText(target.item || row?.activityLabel || ''),
  };
}

// ✅ tenant prefix for raw fetches and navigation.
function tenantBase() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    if (m) return m[0];

    const slug = readTenantSlugFromStorage();
    return slug ? `/t/${slug}` : '';
  } catch {
    return '';
  }
}

// ✅ helper: keep /t/:slug on app navigation without double-prefixing.
function withTenantRoute(path) {
  const base = tenantBase();
  const p = String(path || '');
  const clean = p.startsWith('/') ? p : `/${p}`;
  if (/^\/t\/[^/]+(\/|$)/.test(clean)) return clean;
  return `${base}${clean}`.replace(/\/{2,}/g, '/');
}

// ✅ dev-safe API origin for raw fetches (mirrors http.js logic)
function apiOrigin() {
  const envBaseRaw = (process.env.REACT_APP_API_BASE || '').trim();
  const envBase = envBaseRaw.replace(/\/+$/, '');

  // If env base is relative ("/api" etc.), it will resolve to localhost:3000 in CRA.
  // Treat as unset so dev fallback below works.
  if (envBase && envBase.startsWith('/')) {
    // fall through to dev fallback / origin
  } else if (envBase) {
    // If explicitly set to CRA dev server, override to backend in dev
    try {
      const { origin, port, hostname, protocol } = window.location;
      if (
        (hostname === 'localhost' || hostname === '127.0.0.1') &&
        String(port) === '3000'
      ) {
        const craOrigin = origin || `${protocol}//${hostname}:3000`;
        if (
          envBase.startsWith(craOrigin) ||
          /^https?:\/\/(localhost|127\.0\.0\.1):3000$/i.test(envBase)
        ) {
          return `${protocol}//${hostname}:5000`;
        }
      }
    } catch {}
    return envBase;
  }

  // CRA dev server commonly runs at :3000 and backend at :5000
  try {
    const { origin, port, hostname, protocol } = window.location;
    if (String(port) === '3000') return `${protocol}//${hostname}:5000`;
    return origin;
  } catch {
    return '';
  }
}

function lc(v) {
  return String(v || '').trim().toLowerCase();
}

function asArray(value) {
  return Array.isArray(value) ? value : value ? [value] : [];
}

function rowOwnedByUser(row, user) {
  const meName = lc(nameOf(user));
  const meEmail = lc(user?.email);
  const meId = lc(user?._id || user?.id || user?.userId);

  const nameCandidates = [
    row?.createdByName,
    row?.createdBy,
    row?.doctorName,
    row?.doctor,
    row?.enteredByName,
    row?.enteredBy,
    row?.userName,
    row?.user,
    row?.internName,
    row?.intern,
    row?.assignedToName,
    row?.assignedTo,
    row?.performedByName,
    row?.performedBy,
    row?.observerName,
    row?.authorName,
    row?.ownerName,
    row?.submittedByName,
    row?.submittedBy,
    row?.clinicianName,
    row?.providerName,
    row?.createdBy?.name,
    row?.createdBy?.fullName,
    row?.createdBy?.displayName,
    row?.createdBy?.email,
    row?.user?.name,
    row?.user?.fullName,
    row?.user?.displayName,
    row?.user?.email,
    row?.internUser?.name,
    row?.internUser?.fullName,
    row?.internUser?.email,
  ];

  const idCandidates = [
    row?.createdById,
    row?.userId,
    row?.internId,
    row?.ownerId,
    row?.submittedById,
    row?.doctorId,
    row?.createdBy?._id,
    row?.user?._id,
    row?.internUser?._id,
  ];

  const names = nameCandidates
    .flatMap((value) => asArray(value))
    .map((value) => lc(value))
    .filter(Boolean);

  const ids = idCandidates
    .flatMap((value) => asArray(value))
    .map((value) => lc(value))
    .filter(Boolean);

  if (meId && ids.some((value) => value === meId)) return true;
  if (meEmail && names.some((value) => value === meEmail || value.includes(meEmail))) return true;
  if (meName && names.some((value) => value === meName || value.includes(meName))) return true;

  return false;
}

function toYmd(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function rowDate(row) {
  return row?.visitDate || row?.activityDate || row?.date || row?.createdAt || row?.updatedAt || '';
}

function inDateWindow(row, from, to) {
  const ymd = toYmd(rowDate(row));
  if (!ymd) return !from && !to;
  if (from && ymd < from) return false;
  if (to && ymd > to) return false;
  return true;
}
function rowCentre(row) {
  return safe(row?.centre || row?.chc || row?.location || row?.site || row?.healthCentre);
}

function rowMatchesQuery(row, q) {
  const needle = lc(q);
  if (!needle) return true;

  const fields = [
    row?.visitId,
    row?.id,
    row?._id,
    row?.citizenId,
    row?.citizen,
    row?.centre,
    row?.chc,
    row?.location,
    row?.templateTitle,
    row?.title,
    row?.templateName,
    row?.diagnosis,
    row?.returnNote,
    row?.status,
    row?.createdByName,
    row?.doctorName,
    row?.userName,
    row?.internName,
    row?.assignedToName,
    row?.createdBy?.name,
    row?.user?.name,
  ]
    .flatMap((value) => asArray(value))
    .map((value) => lc(value))
    .filter(Boolean);

  return fields.some((value) => value.includes(needle));
}

function filterOwnedRows(rows, user, { centre = '', from = '', to = '', q = '' } = {}) {
  const centreNeedle = lc(centre);
  return coerceList(rows).filter((row) => {
    if (!rowOwnedByUser(row, user)) return false;
    if (centreNeedle) {
      const c = lc(rowCentre(row));
      if (!c.includes(centreNeedle)) return false;
    }
    if (!inDateWindow(row, from, to)) return false;
    if (!rowMatchesQuery(row, q)) return false;
    return true;
  });
}

export default function ApprovalDashboard() {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ NEW: stable "return-to" snapshot (used when navigating into Review/Observer screens)
  const returnTo = useMemo(() => {
    try {
      return `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
    } catch {
      return '';
    }
  }, [location.pathname, location.search, location.hash]);

  const user = useMemo(() => readStoredUser(), []);
  const myRole = roleOf(user);
  const myName = nameOf(user);
  const myId = String(user?._id || user?.id || user?.userId || user?.user?._id || '').trim();
  const isIntern = myRole.includes('intern');
  const isAdminLike = ['admin', 'owner', 'superadmin', 'super admin'].includes(myRole);
  const requiresAssignedReviewer = ['junior resident', 'senior resident', 'faculty'].includes(myRole);

  const urlTab = new URLSearchParams(location.search).get('tab');
  const defaultToMine =
    !isIntern &&
    /(?:^|\/)(dashboard\/approval|approval)$/.test(String(location.pathname || ''));
  const initialTab = ['pending', 'mine', 'returned'].includes(String(urlTab))
    ? urlTab
    : isIntern
    ? 'returned'
    : defaultToMine
    ? 'mine'
    : 'pending';
  const [tab, setTab] = useState(initialTab);

  const pageTitle =
    isIntern && tab === 'returned'
      ? 'Returned to Edit'
      : !isIntern && tab === 'mine'
      ? 'Reviewer Workbench'
      : !isIntern
      ? 'Reviewer Workbench'
      : 'Approval Dashboard';

  const pageIntro = isIntern
    ? 'Use this page for returned OPD Visits that need correction. Returned Guided Activities remain a separate workflow.'
    : 'Review assigned or centre-pending OPD Visits, flag precise corrections with instructions, approve completed Visits, and review your work history.';

  const [counts, setCounts] = useState({ pending: 0, mine: 0, returned: 0 });
  const [centre, setCentre] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});
  const [err, setErr] = useState('');

  // ---- Return Visit for Correction dialog state ----
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnNote, setReturnNote] = useState('');
  const [returnForVisitId, setReturnForVisitId] = useState(null);
  const [returnRow, setReturnRow] = useState(null);
  const [returnItems, setReturnItems] = useState([emptyCorrectionItem()]);
  const [returnImportedItems, setReturnImportedItems] = useState([]);

  // ---- Guided Activities observer queue state ----
  const [activityRows, setActivityRows] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityErr, setActivityErr] = useState('');
  const [guidedQueueFilter, setGuidedQueueFilter] = useState('all');
  const [sessionRows, setSessionRows] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionErr, setSessionErr] = useState('');

  const sessionInternNames = useMemo(() => {
    const byActivityId = new Map();
    const byUserId = new Map();

    (sessionRows || []).forEach((session) => {
      (Array.isArray(session?.participants) ? session.participants : []).forEach((participant) => {
        const participantType = String(participant?.participantType || participant?.role || '').toLowerCase();
        const functions = Array.isArray(participant?.functions) ? participant.functions : [];
        const isInternParticipant = participantType.includes('intern') ||
          functions.some((fn) => String(fn || '').toLowerCase().includes('intern participant'));
        if (!isInternParticipant) return;

        const name = readablePersonName(participant);
        if (!name) return;

        const activityId = stableId(participant?.linkedActivityInstanceId);
        const userId = stableId(participant?.userId || participant?.internUserId);
        if (activityId) byActivityId.set(activityId, name);
        if (userId) byUserId.set(userId, name);
      });
    });

    return { byActivityId, byUserId };
  }, [sessionRows]);

  const guidedQueueSummary = useMemo(() => {
    const summary = { all: 0, pending: 0, returned: 0, approved: 0, other: 0 };

    (activityRows || []).forEach((row) => {
      summary.all += 1;
      const bucket = guidedReviewBucket(activityStatus(row));
      if (Object.prototype.hasOwnProperty.call(summary, bucket)) summary[bucket] += 1;
      else summary.other += 1;
    });

    return summary;
  }, [activityRows]);

  const filteredActivityRows = useMemo(() => {
    if (guidedQueueFilter === 'all') return activityRows || [];
    return (activityRows || []).filter(
      (row) => guidedReviewBucket(activityStatus(row)) === guidedQueueFilter
    );
  }, [activityRows, guidedQueueFilter]);

  // ---- Plan / Record Activity session dialog ----
  const [planOpen, setPlanOpen] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const [planErr, setPlanErr] = useState('');
  const [planTemplates, setPlanTemplates] = useState([]);
  const [planTemplateId, setPlanTemplateId] = useState('');
  const [planTiming, setPlanTiming] = useState('planned');
  const [planCentre, setPlanCentre] = useState('');
  const [planDate, setPlanDate] = useState(toYmd(new Date()));
  const [planShift, setPlanShift] = useState('');
  const [planCandidates, setPlanCandidates] = useState({ interns: [], staff: [] });
  const [planInternModes, setPlanInternModes] = useState({});
  const [planStaffFlags, setPlanStaffFlags] = useState({});
  const [planNotes, setPlanNotes] = useState('');
  const [myVisitRows, setMyVisitRows] = useState([]);
  const [myActivityRows, setMyActivityRows] = useState([]);
  const [myWorkLoading, setMyWorkLoading] = useState(false);
  const [myWorkErr, setMyWorkErr] = useState('');

  const containerRef = useRef(null);
  const [maxH, setMaxH] = useState(520);
  useEffect(() => {
    const recalc = () => {
      const el = containerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setMaxH(Math.max(260, window.innerHeight - top - 24));
    };
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, []);

  const withMineParams = (params) => {
    params.append('mine', '1');
    const nm = myName && myName.trim();
    if (nm) params.append('userName', nm);
    return params;
  };

  const goComprehensiveServices = () => navigate(withTenantRoute('/dashboard/operations'));

  // ---------- backend calls for VISIT prescriptions ----------
  const getPending = async (query) => {
    const qstr = `${query}&cb=${Date.now()}`;
    try {
      const res = await http.get(`/api/visit-prescriptions/pending?${qstr}`);
      return coerceList(res.data);
    } catch {}
    try {
      const res = await http.get(`/api/approvals/pending?${qstr}`);
      return coerceList(res.data);
    } catch {}
    try {
      const res = await http.get(`/api/visit-prescription/pending?${qstr}`);
      return coerceList(res.data);
    } catch {}
    return [];
  };
  const getReturned = async (query) => {
    const qstr = `${query}&cb=${Date.now()}`;
    try {
      const res = await http.get(`/api/visit-prescriptions/returned?${qstr}`);
      return coerceList(res.data);
    } catch {}
    try {
      const res = await http.get(`/api/approvals/returned?${qstr}`);
      return coerceList(res.data);
    } catch {}
    try {
      const res = await http.get(`/api/visit-prescription/returned?${qstr}`);
      return coerceList(res.data);
    } catch {}
    return [];
  };

  const fetchCounts = async () => {
    try {
      const pendParams = new URLSearchParams();
      pendParams.append('limit', '200');
      if (isIntern) withMineParams(pendParams);
      else pendParams.append('queue', 'centre');
      const pendList = await getPending(pendParams.toString());
      const pending = pendList.length;

      const retParams = new URLSearchParams();
      retParams.append('limit', '200');
      if (isIntern) withMineParams(retParams);
      const retList = await getReturned(retParams.toString());

      let mine = 0;
      if (isIntern) {
        mine = pending;
      } else {
        const mineParams = new URLSearchParams();
        mineParams.append('limit', '200');
        mineParams.append('mine', '1');
        const mineList = await getPending(mineParams.toString());
        mine = mineList.length;
      }

      setCounts({ pending, mine, returned: retList.length });
    } catch (e) {
      setErr(e?.message || 'Failed to load counters');
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const sendCentreFilter = Boolean(centre && (isIntern || isAdminLike || tab === 'returned'));
      if (sendCentreFilter) params.append(tab === 'returned' ? 'centre' : 'chc', centre);
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      if (q) params.append('q', q);
      params.append('limit', '200');

      if (tab === 'pending' || tab === 'mine') {
        if (isIntern) {
          withMineParams(params);
        } else if (tab === 'mine') {
          params.append('mine', '1');
        } else {
          params.append('queue', 'centre');
        }
        const list = await getPending(params.toString());
        setRows(list);
      } else {
        if (isIntern) withMineParams(params);
        const list = await getReturned(params.toString());
        setRows(list);
      }
    } catch (e) {
      setErr(e?.message || 'Failed to load rows');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyWork = async () => {
    if (isIntern) {
      setMyVisitRows([]);
      setMyActivityRows([]);
      setMyWorkErr('');
      setMyWorkLoading(false);
      return;
    }

    try {
      setMyWorkLoading(true);
      setMyWorkErr('');

      const filterState = { centre, from, to, q };

      const myVisitsPromise = http
        .get('/api/visits', { params: { limit: 200, cb: Date.now() } })
        .then((res) =>
          filterOwnedRows(res?.data, user, filterState).sort(
            (a, b) => new Date(b?.visitDate || b?.createdAt || 0) - new Date(a?.visitDate || a?.createdAt || 0)
          )
        )
        .catch(() => []);

      const myInternActivitiesPromise = http
        .get('/api/intern-activities', { params: { limit: 200, cb: Date.now() } })
        .then((res) =>
          filterOwnedRows(res?.data, user, filterState).map((row) => ({ ...row, __kind: 'intern' }))
        )
        .catch(() => []);

      const myGuidedActivitiesPromise = activityApi
        .listMine({ limit: 200, cb: Date.now() })
        .then((res) =>
          filterOwnedRows(coerceList(res), user, filterState).map((row) => ({ ...row, __kind: 'guided' }))
        )
        .catch(async () => {
          try {
            const res = await activityApi.listAll({ limit: 200, cb: Date.now() });
            return filterOwnedRows(coerceList(res), user, filterState).map((row) => ({ ...row, __kind: 'guided' }));
          } catch {
            return [];
          }
        });

      const [myVisits, myInternActivities, myGuidedActivities] = await Promise.all([
        myVisitsPromise,
        myInternActivitiesPromise,
        myGuidedActivitiesPromise,
      ]);

      const mergedActivities = [...coerceList(myInternActivities), ...coerceList(myGuidedActivities)].sort(
        (a, b) => new Date(b?.date || b?.activityDate || b?.createdAt || 0) - new Date(a?.date || a?.activityDate || a?.createdAt || 0)
      );

      setMyVisitRows(myVisits);
      setMyActivityRows(mergedActivities);
    } catch (e) {
      setMyVisitRows([]);
      setMyActivityRows([]);
      setMyWorkErr(e?.message || 'Failed to load your work');
    } finally {
      setMyWorkLoading(false);
    }
  };

  // ---------- fetch guided-activity observer queue (JR/SR/Faculty) ----------
  const fetchActivityQueue = async () => {
    if (isIntern) {
      setActivityRows([]);
      setActivityErr('');
      setActivityLoading(false);
      return;
    }

    try {
      setActivityErr('');
      setActivityLoading(true);

      // Load reviewer-visible guided history across actionable and completed states.
      // Backend status aliases preserve compatibility with older/newer workflow values.
      const data = await activityApi.reviewerQueue({
        status:
          'submitted,pending,awaiting-review,awaiting-observer,observed-submitted,returned,returned-to-edit,observed-returned,repeat,observed-repeat,repeat-required,approved,observed-approved',
        limit: 500,
        cb: Date.now(),
      });

      const list = coerceList(data).map((row) => ({ ...row, __kind: 'guided' }));
      setActivityRows(list);
    } catch (e) {
      setActivityErr(e?.message || 'Failed to load guided activities');
      setActivityRows([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchActivitySessions = async () => {
    if (isIntern) {
      setSessionRows([]);
      setSessionErr('');
      setSessionLoading(false);
      return;
    }
    try {
      setSessionLoading(true);
      setSessionErr('');
      const data = await activityApi.listSessions({ limit: 100, cb: Date.now() });
      setSessionRows(coerceList(data));
    } catch (e) {
      setSessionRows([]);
      setSessionErr(e?.message || 'Failed to load activity teams / assignments');
    } finally {
      setSessionLoading(false);
    }
  };

  const selectedPlanTemplate = useMemo(
    () => planTemplates.find((item) => String(item?._id || item?.id) === String(planTemplateId)) || null,
    [planTemplates, planTemplateId]
  );

  const planParticipationModes = useMemo(
    () => templateParticipationModes(selectedPlanTemplate || {}),
    [selectedPlanTemplate]
  );

  const loadPlanTemplates = async () => {
    try {
      const data = await activityApi.listActiveTemplates({ active: true, limit: 500 });
      const items = coerceList(data).filter((item) => String(item?.sourceFlow || 'guided') !== 'opd');
      setPlanTemplates(items);
      if (!planTemplateId && items[0]) setPlanTemplateId(String(items[0]._id || items[0].id || ''));
    } catch (e) {
      setPlanErr(e?.message || 'Failed to load activity templates');
    }
  };

  const loadPlanCandidates = async () => {
    if (!planOpen || !planCentre || !planDate) return;
    try {
      setPlanErr('');
      const data = await activityApi.teamCandidates({ centre: planCentre, date: planDate });
      setPlanCandidates({
        interns: Array.isArray(data?.interns) ? data.interns : [],
        staff: Array.isArray(data?.staff) ? data.staff : [],
      });
    } catch (e) {
      setPlanCandidates({ interns: [], staff: [] });
      setPlanErr(e?.message || 'Failed to load available activity-team members');
    }
  };

  const openPlanActivity = () => {
    setPlanErr('');
    setPlanTiming('planned');
    setPlanCentre(centre || '');
    setPlanDate(toYmd(new Date()));
    setPlanShift('');
    setPlanInternModes({});
    setPlanStaffFlags({});
    setPlanNotes('');
    setPlanOpen(true);
  };

  const closePlanActivity = () => {
    if (planBusy) return;
    setPlanOpen(false);
    setPlanErr('');
  };

  const togglePlanIntern = (internId, checked) => {
    setPlanInternModes((current) => {
      const next = { ...current };
      if (!checked) delete next[internId];
      else next[internId] = next[internId] || planParticipationModes[0]?.key || '';
      return next;
    });
  };

  const setPlanInternMode = (internId, mode) => {
    setPlanInternModes((current) => ({ ...current, [internId]: normalizeParticipationMode(mode) }));
  };

  const setPlanStaffFlag = (staffId, key, checked) => {
    setPlanStaffFlags((current) => ({
      ...current,
      [staffId]: { ...(current[staffId] || {}), [key]: checked },
    }));
  };

  const submitPlanActivity = async () => {
    try {
      setPlanErr('');
      if (!planTemplateId) throw new Error('Select an activity.');
      if (!planCentre) throw new Error('Enter/select the centre.');
      if (!planDate) throw new Error('Select the activity date.');

      const participants = [];
      const activityLeadIds = [];
      const overallSupervisorIds = [];
      const observerIds = [];

      planCandidates.staff.forEach((staff) => {
        const id = String(staff?.userId || staff?._id || '');
        const flags = planStaffFlags[id] || {};
        const included = flags.member || flags.lead || flags.supervisor || flags.observer;
        if (!included) return;
        const functions = [];
        if (flags.lead) { functions.push('Activity lead'); activityLeadIds.push(id); }
        if (flags.supervisor) { functions.push('Overall supervisor'); overallSupervisorIds.push(id); }
        if (flags.observer) { functions.push('Observer'); observerIds.push(id); }
        if (!functions.length) functions.push('Activity team member');
        participants.push({ userId: id, functions });
      });

      let selectedInternCount = 0;
      let supervisorRequiredForSelectedIntern = false;
      planCandidates.interns.forEach((intern) => {
        const id = String(intern?.userId || intern?._id || '');
        if (!Object.prototype.hasOwnProperty.call(planInternModes, id)) return;
        selectedInternCount += 1;
        const mode = normalizeParticipationMode(planInternModes[id]);
        if (planParticipationModes.length && !mode) {
          throw new Error(`Select nominated participation for ${intern?.name || 'the intern'}.`);
        }
        const modeConfig = participationModeConfig(selectedPlanTemplate || {}, mode);
        if (modeConfig?.requiresSupervisor === true) supervisorRequiredForSelectedIntern = true;
        participants.push({
          userId: id,
          functions: ['Intern participant'],
          plannedParticipationMode: mode,
        });
      });

      if (!participants.length) throw new Error('Select at least one team participant.');
      if (!activityLeadIds.length) throw new Error('Select at least one activity lead from the staff team.');
      if (selectedInternCount > 0 && !observerIds.length) {
        throw new Error('Select at least one observer for an activity involving interns.');
      }
      if (supervisorRequiredForSelectedIntern && !overallSupervisorIds.length) {
        throw new Error('Select at least one supervisor for the nominated intern participation level.');
      }

      setPlanBusy(true);
      await activityApi.createSession({
        templateId: planTemplateId,
        timingState: planTiming,
        centre: planCentre,
        date: planDate,
        plannedAt: planTiming === 'planned' ? planDate : undefined,
        occurredAt: planTiming === 'already_performed' ? planDate : undefined,
        shift: planShift,
        activityLeadIds: activityLeadIds.filter(Boolean),
        overallSupervisorIds,
        observerIds,
        participants,
        notes: planNotes,
      });
      setPlanOpen(false);
      await fetchActivitySessions();
      await fetchActivityQueue();
    } catch (e) {
      setPlanErr(e?.message || 'Failed to create activity session');
    } finally {
      setPlanBusy(false);
    }
  };

  useEffect(() => {
    if (!planOpen) return;
    if (!planTemplates.length) void loadPlanTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planOpen]);

  useEffect(() => {
    if (!planOpen || !planCentre || !planDate) return;
    const timer = setTimeout(() => void loadPlanCandidates(), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planOpen, planCentre, planDate]);

  useEffect(() => {
    fetchCounts();
    fetchRows();
    fetchMyWork();
    fetchActivityQueue();
    fetchActivitySessions();
    // eslint-disable-next-line
  }, [tab, centre, from, to, q]);

  const refreshAll = () => {
    fetchCounts();
    fetchRows();
    fetchMyWork();
    fetchActivityQueue();
    fetchActivitySessions();
  };

  // ======== Robust "open" behaviour for VISITS ========
  const openPDFBlob = async (visitId) => {
    const token = getPortalToken();

    const base = apiOrigin();
    const tbase = tenantBase();
    const url = `${base}${tbase}/api/visits/${encodeURIComponent(
      visitId
    )}/prescription/pdf?cb=${Date.now()}`;

    const r = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });

    if (!r.ok) throw new Error(`PDF ${r.status}`);
    const b = await r.blob();
    const objUrl = URL.createObjectURL(b);
    window.open(objUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(objUrl), 30000);
  };

  const seedVisitNavigationContext = (citizenId, visitId, extra = {}) => {
    const cid = identifierText(citizenId, ['citizenId']);
    const vid = identifierText(visitId, ['visitId']);
    if (!cid || !vid) return;

    try {
      localStorage.setItem('activeCitizenId', cid);
      localStorage.setItem('citizenId', cid);
      localStorage.setItem(`activeVisitId:${cid}`, vid);

      Object.entries(extra || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === false) return;
        localStorage.setItem(key, String(value));
      });
    } catch {}
  };

  // Default: open read-only summary (kept for non-Intern flows)
  const openSummary = async (row) => {
    const visitId = visitIdFromRow(row);
    if (!visitId) {
      setErr('Missing visitId');
      return;
    }

    const cid =
      citizenIdFromRow(row) ||
      localStorage.getItem('activeCitizenId') ||
      localStorage.getItem('citizenId') ||
      '';

    if (cid) {
      seedVisitNavigationContext(cid, visitId);

      const qs = new URLSearchParams({ visitId, from: 'approvals' }).toString();
      navigate(
        withTenantRoute(`/citizen/${encodeURIComponent(cid)}/visit-summary?${qs}`),
        { state: { from: returnTo } }
      );
      return;
    }

    try {
      await openPDFBlob(visitId);
    } catch (e) {
      setErr(e?.message || 'Could not open visit');
    }
  };

  const openReviewWorkspace = (row) => {
    const visitId = visitIdFromRow(row);
    const cid = citizenIdFromRow(row);
    if (!visitId || !cid) {
      setErr('Missing citizen/visit');
      return;
    }

    setErr('');
    seedVisitNavigationContext(cid, visitId);

    navigate(
      withTenantRoute(
        `/citizen/${encodeURIComponent(cid)}/visit/${encodeURIComponent(visitId)}/workspace?section=history&mode=readonly&review=1`
      ),
      {
        state: {
          from: returnTo,
          readOnly: true,
          reviewerMode: true,
          visitId,
          citizenId: cid,
        },
      }
    );
  };

  // Intern + Returned: open EDITOR (seed flag + route to history)
  const openEditorReturned = (row) => {
    const visitId = visitIdFromRow(row);
    const cid = citizenIdFromRow(row);
    if (!visitId || !cid) {
      setErr('Missing citizen/visit');
      return;
    }

    setErr('');
    seedVisitNavigationContext(cid, visitId, { editReturnedVisit: visitId });

    navigate(
      withTenantRoute(
        `/citizen/${encodeURIComponent(cid)}/visit/${encodeURIComponent(visitId)}/workspace?section=history`
      ),
      { state: { from: returnTo, visitId, citizenId: cid, returnedEditor: true } }
    );
  };

  // Guided activity: open in ObserverSignoff view. Own work opens evidence-only.
  const openActivity = (row, opts = {}) => {
    const actId = row?._id || row?.id || row?.activityId;
    if (!actId) {
      setErr('Missing activity id');
      return;
    }
    navigate(
      withTenantRoute(`/intern/activity/observe/${encodeURIComponent(actId)}`),
      { state: { from: returnTo, viewOnly: !!opts.viewOnly } }
    );
  };

  async function approveOnServer(visitId, payload) {
    try {
      await http.patch(
        `/api/visits/${encodeURIComponent(visitId)}/prescription/approve`,
        payload
      );
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      if (status && ![404, 405].includes(status)) throw error;
      await http.post(
        `/api/visit-prescription/${encodeURIComponent(visitId)}/approve`,
        payload
      );
    }
  }
  async function returnOnServer(visitId, payload) {
    try {
      await http.patch(
        `/api/visits/${encodeURIComponent(visitId)}/prescription/return`,
        payload
      );
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      if (status && ![404, 405].includes(status)) throw error;
      await http.post(
        `/api/visit-prescription/${encodeURIComponent(visitId)}/return`,
        payload
      );
    }
  }

  async function takeOverOnServer(visitId) {
    try {
      await http.patch(`/api/visits/${encodeURIComponent(visitId)}/prescription/takeover`, {});
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      if (status && ![404, 405].includes(status)) throw error;
      await http.post(`/api/visit-prescription/${encodeURIComponent(visitId)}/takeover`, {});
    }
  }

  const doTakeOver = async (row) => {
    const visitId = visitIdFromRow(row);
    if (!visitId || acting[visitId]) return;
    setActing((state) => ({ ...state, [visitId]: true }));
    setErr('');
    try {
      await takeOverOnServer(visitId);
      await fetchCounts();
      await fetchRows();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Take over failed');
    } finally {
      setActing((state) => ({ ...state, [visitId]: false }));
    }
  };

  const decPendingCounters = () => {
    setCounts((c) => ({
      pending: Math.max(0, (c.pending || 0) - 1),
      mine: tab === 'mine' ? Math.max(0, (c.mine || 0) - 1) : c.mine || 0,
      returned: c.returned || 0,
    }));
  };

  const doApprove = async (row) => {
    const visitId = visitIdFromRow(row);
    if (!visitId || acting[visitId]) return;
    setActing((s) => ({ ...s, [visitId]: true }));
    setErr('');
    try {
      await approveOnServer(visitId, { role: user.role, actor: user.name });
      setRows((prev) => prev.filter((r) => (r.visitId || r.id) !== visitId));
      decPendingCounters();
      await fetchCounts();
      await fetchRows();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Approve failed');
    } finally {
      setActing((s) => ({ ...s, [visitId]: false }));
    }
  };

  // ---- Return Visit for Correction dialog ----
  const openReturnDialog = (row) => {
    const visitId = visitIdFromRow(row);
    if (!visitId || acting[visitId]) return;

    const rowItems = correctionItemsFromRow(row);
    const workspaceFlags = readStoredReviewFlags(visitId);
    const importedItems = mergeCorrectionItems(workspaceFlags, rowItems);
    const initialItems = importedItems.length ? importedItems : [emptyCorrectionItem()];

    setReturnForVisitId(visitId);
    setReturnRow(row || null);
    setReturnNote('');
    setReturnImportedItems(workspaceFlags);
    setReturnItems(initialItems);
    setReturnOpen(true);
  };

  const closeReturnDialog = () => {
    setReturnOpen(false);
    setReturnNote('');
    setReturnForVisitId(null);
    setReturnRow(null);
    setReturnItems([emptyCorrectionItem()]);
    setReturnImportedItems([]);
  };

  const addReturnItem = () => {
    setReturnItems((current) => [...current, emptyCorrectionItem()]);
  };

  const updateReturnItem = (index, key, value) => {
    setReturnItems((current) =>
      current.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: value } : item
      ))
    );
  };

  const removeReturnItem = (index) => {
    setReturnItems((current) => {
      const next = current.filter((_item, itemIndex) => itemIndex !== index);
      return next.length ? next : [emptyCorrectionItem()];
    });
  };

  const normalizedReturnItems = () =>
    returnItems
      .map(normalizeCorrectionItem)
      .filter((item) => item.section && item.instruction);

  const submitReturn = async () => {
    const visitId = returnForVisitId;
    if (!visitId || acting[visitId]) return;

    const correctionItems = normalizedReturnItems();
    if (!correctionItems.length) {
      setErr('Add at least one section and a clear correction instruction before returning the Visit.');
      return;
    }

    setActing((s) => ({ ...s, [visitId]: true }));
    setErr('');
    try {
      const structuredLines = correctionItems.map((item) => {
        const sectionLabel =
          RETURN_SECTION_OPTIONS.find((option) => option.value === item.section)?.label ||
          item.section;
        const target = item.field ? `${sectionLabel} → ${item.field}` : sectionLabel;
        return `${target}: ${item.instruction}`;
      });
      const generalNote = returnNote?.trim() || '';
      const combinedNote = [generalNote, ...structuredLines].filter(Boolean).join('\n');
      const returnSections = Array.from(
        new Set(correctionItems.map((item) => item.section).filter((section) => section !== 'general'))
      );

      const payload = {
        role: user.role,
        actor: user.name,
        note: combinedNote,
        returnNote: combinedNote,
        correctionItems,
        returnItems: correctionItems,
        flaggedCorrections: correctionItems,
        returnSections,
      };

      await returnOnServer(visitId, payload);
      clearStoredReviewFlags(visitId);
      setRows((prev) => prev.filter((r) => (r.visitId || r.id) !== visitId));
      decPendingCounters();
      await fetchCounts();
      await fetchRows();
      closeReturnDialog();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Return failed');
    } finally {
      setActing((s) => ({ ...s, [visitId]: false }));
    }
  };

  const headerBg = (t) =>
    t.palette.mode === 'dark' ? 'background.default' : 'grey.50';
  const shellBg = (t) =>
    t.palette.mode === 'dark' ? 'background.paper' : 'background.default';
  const COLSPAN = tab === 'returned' ? 7 : 8;

  // ✅ NEW: tenant-safe search navigation (keeps /t/:slug always)
  const setTabAndUrl = (nextTab) => {
    setTab(nextTab);
    if (!isAdminLike && (nextTab === 'pending' || nextTab === 'mine')) setCentre('');
    const search = `?tab=${encodeURIComponent(nextTab)}`;
    navigate({ pathname: location.pathname, search }, { replace: true });
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, pb: 2 }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h4" sx={{ mb: 0.75 }}>
          {pageTitle}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, mb: 1.5 }}>
          {pageIntro}
        </Typography>
        {isIntern ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" startIcon={<LaunchIcon />} onClick={goComprehensiveServices}>
              Comprehensive Services
            </Button>
          </Stack>
        ) : null}
      </Box>

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      <Stack direction="row" spacing={1.25} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`${isIntern ? 'Pending' : 'Centre pending'}: ${counts.pending}`}
          color={tab === 'pending' ? 'primary' : 'default'}
          onClick={() => setTabAndUrl('pending')}
          clickable
          variant={tab === 'pending' ? 'filled' : 'outlined'}
        />
        <Chip
          label={`${isIntern ? 'Mine' : 'Assigned to me'}: ${counts.mine}`}
          color={tab === 'mine' ? 'primary' : 'default'}
          onClick={() => setTabAndUrl('mine')}
          clickable
          variant={tab === 'mine' ? 'filled' : 'outlined'}
        />
        <Chip
          label={`Returned: ${counts.returned}`}
          color={tab === 'returned' ? 'primary' : 'default'}
          onClick={() => setTabAndUrl('returned')}
          clickable
          variant={tab === 'returned' ? 'filled' : 'outlined'}
        />
        {!isIntern && (
          <Chip
            label={`Guided pending: ${guidedQueueSummary.pending}`}
            color="default"
            variant="outlined"
          />
        )}
        <IconButton size="small" onClick={refreshAll} title="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* VISIT APPROVALS */}
      <Box sx={{ mt: 0.5, mb: 1 }}>
        <Typography variant="h6">Visit Approval Queue</Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Review and act on citizen visit prescriptions and related visit workflow submissions.
        </Typography>
      </Box>
      <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: shellBg }}>
        <Box
          sx={(theme) => ({
            position: 'sticky',
            top: 0,
            zIndex: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            p: { xs: 1.5, md: 2 },
            bgcolor: headerBg(theme),
          })}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems="center"
          >
            <TextField
              label={isAdminLike ? 'Centre' : (tab === 'pending' || tab === 'mine' ? 'Centre (current duty scope)' : 'Centre')}
              size="small"
              value={centre}
              onChange={(e) => setCentre(e.target.value)}
              disabled={!isAdminLike && (tab === 'pending' || tab === 'mine')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 260 } }}
            />
            <TextField
              label="From"
              type="date"
              size="small"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Search"
              size="small"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refreshAll}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <TableContainer ref={containerRef} sx={{ maxHeight: maxH, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Visit ID</TableCell>
                <TableCell>Citizen ID</TableCell>
                {tab !== 'returned' && <TableCell>Centre</TableCell>}
                <TableCell>Visit Date</TableCell>
                <TableCell>Diagnosis / Diff Dx</TableCell>
                <TableCell>Status</TableCell>
                {tab !== 'returned' && <TableCell>Assigned reviewer</TableCell>}
                {tab === 'returned' && <TableCell>Return Note</TableCell>}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={COLSPAN}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2 }}>
                      <CircularProgress size={20} />
                      <Typography>Loading…</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLSPAN}>
                    <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      {tab === 'returned'
                        ? isIntern
                          ? 'Nothing returned to you.'
                          : 'No returned prescriptions.'
                        : tab === 'mine'
                        ? 'No items assigned to you.'
                        : isIntern
                        ? 'No pending items for you.'
                        : 'No pending Visits at your current duty centre.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {rows.map((r) => {
                const visitId = visitIdFromRow(r);
                const citizenId = citizenIdFromRow(r);
                const centreName = r.centre || r.chc || '';
                const when =
                  r.visitDate || r.createdAt
                    ? new Date(r.visitDate || r.createdAt).toLocaleString()
                    : '-';
                const dx =
                  r.diagnosis ||
                  r?.prescription?.icdCodes?.[0]?.title ||
                  r?.prescription?.differentialDx ||
                  '';
                const rawStatus = r.status || r?.prescription?.status || '';
                const st = statusText(rawStatus);
                const locked = !!r._locked || !!acting[visitId];
                const assignedToId = String(r?.assignedToId || r?.assignedTo?.id || '').trim();
                const assignedToName = readablePersonName(r?.assignedToName || r?.assignedTo) || '';
                const assignedToRole = sanitizeDisplayText(r?.assignedToRole || r?.assignedTo?.role || '');
                const assignedToMe = Boolean(
                  (myId && assignedToId && myId === assignedToId) ||
                  (myName && assignedToName && lc(myName) === lc(assignedToName))
                );
                const needsTakeover = !isIntern && !isAdminLike && requiresAssignedReviewer && !assignedToMe;

                const isReturnedRow = String(rawStatus).toLowerCase() === 'returned';

                const onOpenClick = () => {
                  if (isIntern && (tab === 'returned' || isReturnedRow)) {
                    openEditorReturned(r);
                  } else if (!isIntern) {
                    openReviewWorkspace(r);
                  } else {
                    openSummary(r);
                  }
                };

                return (
                  <TableRow key={visitId} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{visitId}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{citizenId}</TableCell>
                    {tab !== 'returned' && <TableCell>{centreName || '-'}</TableCell>}
                    <TableCell>{when}</TableCell>
                    <TableCell>{dx || <span style={{ color: '#888' }}>—</span>}</TableCell>
                    <TableCell>
                      <Chip label={st} size="small" color={chipColor(st)} />
                    </TableCell>
                    {tab !== 'returned' && (
                      <TableCell>
                        {assignedToName ? (
                          <Stack spacing={0.15}>
                            <span>{assignedToName}</span>
                            {assignedToRole ? <Typography variant="caption" sx={{ opacity: 0.7 }}>{assignedToRole}</Typography> : null}
                          </Stack>
                        ) : (
                          <Chip size="small" variant="outlined" label="Unassigned" />
                        )}
                      </TableCell>
                    )}
                    {tab === 'returned' && (
                      <TableCell>
                        {returnInstructionSummary(r) ? (
                          <Tooltip title={returnInstructionSummary(r)}>
                            <span
                              style={{
                                display: 'inline-block',
                                maxWidth: 360,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {returnInstructionSummary(r)}
                            </span>
                          </Tooltip>
                        ) : (
                          <span style={{ color: '#888' }}>—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Tooltip
                        title={
                          isIntern && (tab === 'returned' || isReturnedRow)
                            ? 'Edit returned Visit'
                            : !isIntern
                              ? 'Review complete Visit in read-only Workspace'
                              : 'Open Visit'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={onOpenClick}
                            aria-label={
                              isIntern && (tab === 'returned' || isReturnedRow)
                                ? `Edit returned Visit ${visitId}`
                                : !isIntern
                                  ? `Review Visit ${visitId} in read-only Workspace`
                                  : `Open Visit ${visitId}`
                            }
                            data-testid={`visit-open-${visitId}`}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {!isIntern ? (
                        <Button size="small" variant="text" onClick={() => openSummary(r)} sx={{ mr: 0.5 }}>
                          Summary
                        </Button>
                      ) : null}

                      {!isIntern && (tab === 'pending' || tab === 'mine') && (
                        needsTakeover ? (
                          <Tooltip title={assignedToName ? `Take over from ${assignedToName}` : 'Take over this unassigned Visit'}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={locked}
                                onClick={() => doTakeOver(r)}
                              >
                                Take Over
                              </Button>
                            </span>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="Approve">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<CheckCircleIcon />}
                                  disabled={locked}
                                  onClick={() => doApprove(r)}
                                  sx={{ mr: 1 }}
                                >
                                  Approve
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Return to edit">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  startIcon={<UndoIcon />}
                                  disabled={locked}
                                  aria-label={`Return Visit ${visitId} for correction`}
                                  onClick={() => openReturnDialog(r)}
                                >
                                  Return
                                </Button>
                              </span>
                            </Tooltip>
                          </>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {tab === 'mine' && !isIntern && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            My Work History
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
            Your own OPD visits and activities are shown here alongside the approval items above.
          </Typography>

          {myWorkErr && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {myWorkErr}
            </Alert>
          )}

          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: shellBg }}>
              <Box sx={{ p: 1.5 }}>
                <Typography variant="subtitle1">My OPD Visits</Typography>
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  Visits created or handled by you.
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 280 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Visit ID</TableCell>
                      <TableCell>Citizen ID</TableCell>
                      <TableCell>Centre</TableCell>
                      <TableCell>Visit Date</TableCell>
                      <TableCell>Diagnosis / Diff Dx</TableCell>
                      <TableCell>Open</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myWorkLoading && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2 }}>
                            <CircularProgress size={20} />
                            <Typography>Loading your OPD visits…</Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}

                    {!myWorkLoading && myVisitRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" sx={{ p: 2, textAlign: 'center', opacity: 0.7 }}>
                            No OPD visits found for you with the current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {!myWorkLoading &&
                      myVisitRows.map((r) => {
                        const visitId = r.visitId || r.id;
                        const citizenId = r.citizenId || r.citizen;
                        const centreName = r.centre || r.chc || '';
                        const when =
                          r.visitDate || r.createdAt
                            ? new Date(r.visitDate || r.createdAt).toLocaleString()
                            : '-';
                        const dx =
                          r.diagnosis ||
                          r?.prescription?.icdCodes?.[0]?.title ||
                          r?.prescription?.differentialDx ||
                          '';

                        return (
                          <TableRow key={`mine-visit-${visitId || Math.random()}`} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{visitId || '-'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{citizenId || '-'}</TableCell>
                            <TableCell>{centreName || '-'}</TableCell>
                            <TableCell>{when}</TableCell>
                            <TableCell>{dx || <span style={{ color: '#888' }}>—</span>}</TableCell>
                            <TableCell>
                              <Tooltip title="Open visit">
                                <span>
                                  <IconButton size="small" onClick={() => openSummary(r)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: shellBg }}>
              <Box sx={{ p: 1.5 }}>
                <Typography variant="subtitle1">My Activities</Typography>
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  Includes legacy intern activities and guided activities recorded by you.
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Activity ID</TableCell>
                      <TableCell>Type / Template</TableCell>
                      <TableCell>Centre</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Open</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myWorkLoading && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2 }}>
                            <CircularProgress size={20} />
                            <Typography>Loading your activities…</Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}

                    {!myWorkLoading && myActivityRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" sx={{ p: 2, textAlign: 'center', opacity: 0.7 }}>
                            No activities found for you with the current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {!myWorkLoading &&
                      myActivityRows.map((a) => {
                        const actId = a._id || a.id || a.activityId;
                        const tplTitle = activityTitle(a);
                        const centreName = sanitizeDisplayText(a.centre || a.chc || a.location || '');
                        const dt = a.date || a.activityDate || a.createdAt || a.updatedAt || '';
                        const when = dt ? new Date(dt).toLocaleString() : '—';
                        const rawActivityStatus = activityStatus(a);
                        const st = statusText(rawActivityStatus);
                        const target = activityReportingTarget(a);

                        return (
                          <TableRow key={`mine-activity-${actId || Math.random()}`} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{actId || '-'}</TableCell>
                            <TableCell>
                              <Stack spacing={0.25}>
                                <span>{tplTitle}</span>
                                {(target.section || target.item) && (
                                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                    {[target.section, target.item].filter(Boolean).join(' · ')}
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>{centreName || '-'}</TableCell>
                            <TableCell>{when}</TableCell>
                            <TableCell>
                              <Chip label={st} size="small" color={chipColor(rawActivityStatus || st)} />
                            </TableCell>
                            <TableCell>
                              {a.__kind === 'guided' ? (
                                <Tooltip title="Open guided activity evidence">
                                  <span>
                                    <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => openActivity(a, { viewOnly: true })}>
                                      Open
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Open activity log">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<VisibilityIcon />}
                                      onClick={() => navigate(withTenantRoute('/intern/activity'), { state: { from: returnTo } })}
                                    >
                                      Open
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        </Box>
      )}

      {!isIntern && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">Activity Teams / Assignments</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Planned and recently recorded real-world activity sessions. Intern evidence remains individual and linked to the team session.
              </Typography>
            </Box>
            <Button variant="contained" size="small" onClick={openPlanActivity}>Plan / Record Activity</Button>
          </Stack>

          {sessionErr && <Alert severity="warning" sx={{ mb: 1.5 }}>{sessionErr}</Alert>}
          <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: shellBg }}>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Activity</TableCell>
                    <TableCell>Centre</TableCell>
                    <TableCell>Timing</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionLoading && (
                    <TableRow><TableCell colSpan={6}><Stack direction="row" spacing={1} sx={{ p: 2 }}><CircularProgress size={20} /><Typography>Loading activity teams…</Typography></Stack></TableCell></TableRow>
                  )}
                  {!sessionLoading && sessionRows.length === 0 && (
                    <TableRow><TableCell colSpan={6}><Typography variant="body2" sx={{ p: 2, textAlign: 'center', opacity: 0.7 }}>No activity sessions recorded yet.</Typography></TableCell></TableRow>
                  )}
                  {!sessionLoading && sessionRows.map((session) => {
                    const when = session?.occurredAt || session?.plannedAt || session?.createdAt;
                    const teamCount = Array.isArray(session?.participants) ? session.participants.length : 0;
                    const internCount = Array.isArray(session?.participants)
                      ? session.participants.filter((p) => String(p?.participantType || '').toLowerCase() === 'intern').length
                      : 0;
                    return (
                      <TableRow key={session?._id || session?.id || Math.random()} hover>
                        <TableCell>{sanitizeDisplayText(session?.templateTitle || 'Activity')}</TableCell>
                        <TableCell>{sanitizeDisplayText(session?.centre || '—')}</TableCell>
                        <TableCell>{session?.timingState === 'already_performed' ? 'Already performed' : 'Planned / will be performed'}</TableCell>
                        <TableCell>{when ? new Date(when).toLocaleString() : '—'}</TableCell>
                        <TableCell>{teamCount} total · {internCount} intern{internCount === 1 ? '' : 's'}</TableCell>
                        <TableCell><Chip size="small" label={sanitizeDisplayText(session?.status || '—')} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Guided Activities observer queue */}
      {!isIntern && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Guided Activity Review
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
            Review submitted guided activities and switch between pending, returned, and approved
            records. My Work remains separate from reviewer-status history. Mapped Core Practice Area
            and rotation-report metadata are shown when available.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={`All: ${guidedQueueSummary.all}`}
              color={guidedQueueFilter === 'all' ? 'primary' : 'default'}
              onClick={() => setGuidedQueueFilter('all')}
              clickable
              variant={guidedQueueFilter === 'all' ? 'filled' : 'outlined'}
            />
            <Chip
              label={`Pending review: ${guidedQueueSummary.pending}`}
              color={guidedQueueFilter === 'pending' ? 'warning' : 'default'}
              onClick={() => setGuidedQueueFilter('pending')}
              clickable
              variant={guidedQueueFilter === 'pending' ? 'filled' : 'outlined'}
            />
            <Chip
              label={`Returned: ${guidedQueueSummary.returned}`}
              color={guidedQueueFilter === 'returned' ? 'error' : 'default'}
              onClick={() => setGuidedQueueFilter('returned')}
              clickable
              variant={guidedQueueFilter === 'returned' ? 'filled' : 'outlined'}
            />
            <Chip
              label={`Approved: ${guidedQueueSummary.approved}`}
              color={guidedQueueFilter === 'approved' ? 'success' : 'default'}
              onClick={() => setGuidedQueueFilter('approved')}
              clickable
              variant={guidedQueueFilter === 'approved' ? 'filled' : 'outlined'}
            />
          </Stack>

          {activityErr && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {activityErr}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: shellBg }}>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Activity ID</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Intern</TableCell>
                    <TableCell>Centre</TableCell>
                    <TableCell>Planned role</TableCell>
                    <TableCell>Intern reported</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityLoading && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2 }}>
                          <CircularProgress size={20} />
                          <Typography>Loading guided activities…</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}

                  {!activityLoading && filteredActivityRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" sx={{ p: 2, textAlign: 'center', opacity: 0.7 }}>
                          {guidedQueueFilter === 'all'
                            ? 'No guided activities found for reviewer history.'
                            : guidedQueueFilter === 'pending'
                            ? 'No guided activities are pending review.'
                            : guidedQueueFilter === 'returned'
                            ? 'No returned or repeat-required guided activities.'
                            : 'No approved guided activities found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {!activityLoading &&
                    filteredActivityRows.map((a) => {
                      const actId = a._id || a.id || a.activityId;
                      const tplTitle = activityTitle(a);
                      const internUserId = stableId(a?.internUserId || a?.internId || a?.userId);
                      const internName =
                        activityInternName(a) ||
                        sessionInternNames.byActivityId.get(stableId(actId)) ||
                        sessionInternNames.byUserId.get(internUserId) ||
                        '';
                      const centreName = sanitizeDisplayText(a.centre || a.chc || a.location || '');
                      const dt = a.date || a.activityDate || a.createdAt || a.updatedAt || '';
                      const when = dt ? new Date(dt).toLocaleString() : '—';
                      const rawActivityStatus = activityStatus(a);
                      const st = statusText(rawActivityStatus);
                      const target = activityReportingTarget(a);

                      return (
                        <TableRow key={actId || Math.random()} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{actId}</TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              <span>{tplTitle}</span>
                              {(target.section || target.item) && (
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                  {[target.section, target.item].filter(Boolean).join(' · ')}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>{internName || '—'}</TableCell>
                          <TableCell>{centreName || '—'}</TableCell>
                          <TableCell>{participationModeLabel(a?.plannedParticipationMode)}</TableCell>
                          <TableCell>{participationModeLabel(a?.reportedParticipationMode)}</TableCell>
                          <TableCell>{when}</TableCell>
                          <TableCell>
                            <Chip label={st} size="small" color={chipColor(rawActivityStatus || st)} />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Open in Observer view">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => openActivity(a)}
                                >
                                  Review
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      <Dialog open={planOpen} onClose={closePlanActivity} fullWidth maxWidth="lg">
        <DialogTitle>Plan / Record Guided Activity</DialogTitle>
        <DialogContent dividers>
          {planErr && <Alert severity="error" sx={{ mb: 2 }}>{planErr}</Alert>}
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
            Create one real-world activity team. Interns receive linked individual guided-activity records; JR/SR/Faculty/HOD and other staff remain team participants without duplicating intern evidence.
          </Typography>

          <Stack spacing={2}>
            <TextField select label="Activity" value={planTemplateId} onChange={(e) => setPlanTemplateId(e.target.value)} fullWidth>
              {planTemplates.map((template) => (
                <MenuItem key={template?._id || template?.id} value={String(template?._id || template?.id || '')}>
                  {sanitizeDisplayText(template?.title || template?.name || 'Activity')}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField select label="Activity timing" value={planTiming} onChange={(e) => setPlanTiming(e.target.value)} sx={{ minWidth: 260 }}>
                <MenuItem value="planned">Will be performed / planned</MenuItem>
                <MenuItem value="already_performed">Already performed</MenuItem>
              </TextField>
              <TextField label="Centre" value={planCentre} onChange={(e) => setPlanCentre(e.target.value)} fullWidth />
              <TextField type="date" label="Date" value={planDate} onChange={(e) => setPlanDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label="Shift (optional)" value={planShift} onChange={(e) => setPlanShift(e.target.value)} />
            </Stack>

            <Box>
              <Typography variant="subtitle1" fontWeight={700}>JR / SR / Faculty / HOD / staff team</Typography>
              <Typography variant="body2" sx={{ opacity: 0.75, mb: 1 }}>
                Select all people participating. One person may be activity lead, supervisor and/or observer when staffing is limited.
              </Typography>
              <Paper variant="outlined">
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow><TableCell>Person</TableCell><TableCell>Team</TableCell><TableCell>Lead</TableCell><TableCell>Supervisor</TableCell><TableCell>Observer</TableCell></TableRow></TableHead>
                    <TableBody>
                      {planCandidates.staff.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" sx={{ p: 1.5, opacity: 0.7 }}>No staff candidates loaded. Enter a centre/date; staff directory remains tenant-wide until the staff roster/attendance module is added.</Typography></TableCell></TableRow>}
                      {planCandidates.staff.map((staff) => {
                        const staffId = String(staff?.userId || staff?._id || '');
                        const flags = planStaffFlags[staffId] || {};
                        return (
                          <TableRow key={staffId}>
                            <TableCell>{sanitizeDisplayText(staff?.name || 'Staff')}<Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>{[staff?.role, staff?.designation, staff?.centre].filter(Boolean).join(' · ')}</Typography></TableCell>
                            {['member', 'lead', 'supervisor', 'observer'].map((key) => (
                              <TableCell key={key}><Checkbox size="small" checked={!!flags[key]} onChange={(e) => setPlanStaffFlag(staffId, key, e.target.checked)} /></TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Interns posted at this centre</Typography>
              <Typography variant="body2" sx={{ opacity: 0.75, mb: 1 }}>
                Nominate one or more interns and the expected capacity. The intern later records what actually happened; the reviewer confirms the final level.
              </Typography>
              <Paper variant="outlined">
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow><TableCell>Select</TableCell><TableCell>Intern</TableCell><TableCell>Shift / posting</TableCell><TableCell>Expected participation</TableCell></TableRow></TableHead>
                    <TableBody>
                      {planCandidates.interns.length === 0 && <TableRow><TableCell colSpan={4}><Typography variant="body2" sx={{ p: 1.5, opacity: 0.7 }}>No on-duty, non-leave interns found for this centre/date.</Typography></TableCell></TableRow>}
                      {planCandidates.interns.map((intern) => {
                        const internId = String(intern?.userId || intern?._id || '');
                        const selected = Object.prototype.hasOwnProperty.call(planInternModes, internId);
                        return (
                          <TableRow key={internId}>
                            <TableCell><Checkbox size="small" checked={selected} onChange={(e) => togglePlanIntern(internId, e.target.checked)} /></TableCell>
                            <TableCell>{sanitizeDisplayText(intern?.name || 'Intern')}<Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>{intern?.rollNo || intern?.batchName || ''}</Typography></TableCell>
                            <TableCell>{[intern?.shift, intern?.posting].filter(Boolean).join(' · ') || '—'}</TableCell>
                            <TableCell sx={{ minWidth: 260 }}>
                              {planParticipationModes.length > 0 ? (
                                <TextField select size="small" fullWidth disabled={!selected} value={selected ? planInternModes[internId] || '' : ''} onChange={(e) => setPlanInternMode(internId, e.target.value)}>
                                  {planParticipationModes.map((mode) => <MenuItem key={mode.key} value={mode.key}>{mode.label || participationModeLabel(mode.key)}</MenuItem>)}
                                </TextField>
                              ) : (
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>Legacy template — role-aware modes not configured yet</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>

            <TextField label="Session / team note (optional)" value={planNotes} onChange={(e) => setPlanNotes(e.target.value)} multiline minRows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePlanActivity} disabled={planBusy}>Cancel</Button>
          <Button variant="contained" onClick={submitPlanActivity} disabled={planBusy}>
            {planBusy ? 'Saving…' : (planTiming === 'already_performed' ? 'Record Activity Team' : 'Plan Activity Team')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Visit for Correction dialog */}
      <Dialog open={returnOpen} onClose={closeReturnDialog} fullWidth maxWidth="md">
        <DialogTitle>Return Visit for Correction</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Flag the exact section and, where possible, the exact field or issue. Add a clear instruction for the Intern.
            You can add more than one correction item.
          </Typography>

          {returnRow ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Visit <strong>{returnForVisitId || '—'}</strong>
              {returnRow?.citizenId || returnRow?.citizen
                ? ` · Citizen ${returnRow?.citizenId || returnRow?.citizen}`
                : ''}
              {returnRow?.centre || returnRow?.chc
                ? ` · ${returnRow?.centre || returnRow?.chc}`
                : ''}
            </Alert>
          ) : null}

          {returnImportedItems.length > 0 && (
            <Alert severity="success" sx={{ mb: 2 }} data-testid="imported-reviewer-corrections">
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Imported from reviewer Workspace
              </Typography>
              <Stack spacing={0.5}>
                {returnImportedItems.map((item, index) => (
                  <Typography
                    key={`imported-correction-${index}`}
                    variant="body2"
                    data-testid={`imported-reviewer-correction-${index}`}
                  >
                    {correctionItemSummary(item)}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <Stack spacing={1.5}>
            {returnItems.map((item, index) => (
              <Paper key={`correction-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <TextField
                      select
                      size="small"
                      label="Section needing correction"
                      data-testid={`return-section-${index}`}
                      value={item.section || ''}
                      onChange={(e) => updateReturnItem(index, 'section', e.target.value)}
                      sx={{ minWidth: { sm: 220 } }}
                    >
                      <MenuItem value="">Select section</MenuItem>
                      {RETURN_SECTION_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      fullWidth
                      label="Exact field / issue (recommended)"
                      data-testid={`return-field-${index}`}
                      value={item.field || ''}
                      onChange={(e) => updateReturnItem(index, 'field', e.target.value)}
                      placeholder="e.g., HOPI → onset, BP, investigation ordered, diagnosis"
                    />
                    <Button
                      size="small"
                      color="error"
                      variant="text"
                      onClick={() => removeReturnItem(index)}
                    >
                      Remove
                    </Button>
                  </Stack>

                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Reviewer instruction"
                    data-testid={`return-instruction-${index}`}
                    value={item.instruction || ''}
                    onChange={(e) => updateReturnItem(index, 'instruction', e.target.value)}
                    placeholder="State exactly what should be clarified, corrected, added or rechecked."
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Button type="button" variant="outlined" size="small" sx={{ mt: 1.5 }} onClick={addReturnItem}>
            Add another flagged item
          </Button>

          <Divider sx={{ my: 2 }} />

          <TextField
            margin="dense"
            label="General note for this returned Visit (optional)"
            type="text"
            fullWidth
            multiline
            minRows={2}
            value={returnNote}
            onChange={(e) => setReturnNote(e.target.value)}
            InputLabelProps={{ shrink: true }}
            placeholder="Use for an overall teaching or handover comment in addition to the specific correction items."
          />

          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.75 }}>
            At least one correction item must include a section and an instruction. Field/issue text is optional when the whole section needs correction.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeReturnDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<UndoIcon />}
            onClick={submitReturn}
            disabled={
              !!(returnForVisitId && acting[returnForVisitId]) ||
              !returnItems.some((item) => String(item?.section || '').trim() && String(item?.instruction || '').trim())
            }
          >
            Return Visit for Correction
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
